function asStr(v) {
  return String(v ?? "").trim();
}

const FROZEN_INVOICE_STATUSES = new Set(["sent", "partially_paid", "paid"]);

/**
 * @param {object | null | undefined} statement
 */
export function isBillingStatementIssuerVatSnapshotActive(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (FROZEN_INVOICE_STATUSES.has(asStr(st.status))) return true;
  return Boolean(asStr(st.invoice_frozen_at));
}

export const TRAINER_INVOICE_VAT_MODES = Object.freeze(["exempt", "standard"]);
export const DEFAULT_GERMAN_INVOICE_VAT_RATE = 19;

/**
 * @param {unknown} trainer
 */
export function resolveTrainerInvoiceVat(trainer) {
  const tr = trainer && typeof trainer === "object" ? trainer : {};
  const mode =
    asStr(tr.invoice_vat_mode) === "standard" ? "standard" : "exempt";
  const rawRate = Number(tr.invoice_vat_rate);
  const rate =
    mode === "standard"
      ? Number.isFinite(rawRate) && rawRate > 0
        ? rawRate
        : DEFAULT_GERMAN_INVOICE_VAT_RATE
      : 0;
  return { mode, rate };
}

/**
 * @param {unknown} mode
 * @param {unknown} rate
 */
export function normalizeTrainerInvoiceVatInput(mode, rate) {
  const normalizedMode = asStr(mode) === "standard" ? "standard" : "exempt";
  if (normalizedMode !== "standard") {
    return { invoice_vat_mode: "exempt", invoice_vat_rate: null };
  }
  const raw = Number(rate);
  const invoice_vat_rate =
    Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_GERMAN_INVOICE_VAT_RATE;
  return { invoice_vat_mode: "standard", invoice_vat_rate };
}

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/**
 * Beträge auf Elternrechnungen sind Brutto; bei USt werden Netto und USt herausgerechnet.
 *
 * @param {number} grossEur
 * @param {{ mode?: string, rate?: number }} vat
 */
export function computeInvoiceVatTotals(grossEur, vat) {
  const gross = round2(Number(grossEur) || 0);
  const mode = asStr(vat?.mode) === "standard" ? "standard" : "exempt";
  const rate =
    mode === "standard"
      ? Number(vat?.rate) || DEFAULT_GERMAN_INVOICE_VAT_RATE
      : 0;
  if (mode !== "standard" || rate <= 0) {
    return {
      netEur: gross,
      vatEur: 0,
      grossEur: gross,
      vatRate: 0,
      showVat: false,
    };
  }
  const netEur = round2(gross / (1 + rate / 100));
  const vatEur = round2(gross - netEur);
  return {
    netEur,
    vatEur,
    grossEur: gross,
    vatRate: rate,
    showVat: true,
  };
}

/**
 * @param {{ mode?: string, rate?: number }} vat
 */
export function resolveInvoiceVatFromStatementSnapshots(
  statement,
  trainerFallback = null,
) {
  const st = statement && typeof statement === "object" ? statement : {};
  if (isBillingStatementIssuerVatSnapshotActive(st)) {
    const snapMode = asStr(st.issuer_vat_mode_snapshot);
    const snapRate = Number(st.issuer_vat_rate_snapshot);
    if (snapMode) {
      return {
        mode: snapMode === "standard" ? "standard" : "exempt",
        rate:
          snapMode === "standard"
            ? Number.isFinite(snapRate)
              ? snapRate
              : DEFAULT_GERMAN_INVOICE_VAT_RATE
            : 0,
      };
    }
  }
  return resolveTrainerInvoiceVat(trainerFallback);
}

/**
 * USt für Rechnungsvorschau: bevorzugt Rechnungssteller, sonst eindeutigen Session-Trainer.
 *
 * @param {{
 *   statement?: object | null,
 *   issuerTrainer?: object | null,
 *   items?: object[],
 *   sessions?: object[],
 *   trainers?: object[],
 * }} input
 */
export function resolveBillingStatementInvoiceVat({
  statement = null,
  issuerTrainer = null,
  items = [],
  sessions = [],
  trainers = [],
} = {}) {
  const issuerVat = resolveInvoiceVatFromStatementSnapshots(
    statement,
    issuerTrainer,
  );
  if (issuerVat.mode === "standard") return issuerVat;

  const sessionList = Array.isArray(sessions) ? sessions : [];
  const trainerList = Array.isArray(trainers) ? trainers : [];
  const sessionById = new Map(sessionList.map((s) => [asStr(s?.id), s]));
  const trainerIds = new Set();

  for (const it of Array.isArray(items) ? items : []) {
    const session = sessionById.get(asStr(it?.session_id));
    const tid = asStr(session?.trainer_id);
    if (tid) trainerIds.add(tid);
  }

  if (trainerIds.size !== 1) return issuerVat;

  const sessionTrainer =
    trainerList.find((tr) => asStr(tr?.id) === [...trainerIds][0]) || null;
  return resolveInvoiceVatFromStatementSnapshots(statement, sessionTrainer);
}
